import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Button,
  Stack,
  Divider,
  Modal,
  Box,
  CircularProgress,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import Logo from "../../components/Logo";
import { useNavigate, useSearchParams } from "react-router-dom";
import { decodeTokenToData } from "../../utils/decodeTokenToData";
import { User, UsersRelation } from "../../api/models/user-model";
import api, {
  CreateAccessKeyRequest,
  CreateAccessPayload,
  CreateAuthorizationPassPayload,
  CreateTransactionPayload,
  CreateVerifiableIdPayload,
  OneTimeVisitorPayload,
} from "../../api";
import { showError } from "../../utils/helpers";
import dayjs from "dayjs";
import { AuthorizationPassRelation } from "../../api/models/authorization-pass";

export interface PassAuthorizationData {
  pass_id: string;
  receiver_id: number;
}

const PassAuthorization = () => {
  const labels = useAppSelector(selectLabels);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [passData, setPassData] = useState<PassAuthorizationData | null>(null);
  const [authorizationPass, setAuthorizationPass] =
    useState<AuthorizationPassRelation | null>(null);
  const [receiverUser, setReceiverUser] = useState<User | null>(null);
  const [visitorInfo, setVisitorInfo] = useState<{
    name: string;
    phone: string;
  } | null>(null);
  const [sendInvitationModal, setSendInvitationModal] = useState(false);
  const [confirmedBy, setConfirmedBy] = useState<UsersRelation | null>(null);
  const [authorized, setAuthorized] = useState(false);
  const [denied, setDenied] = useState(false);
  const [deniedModal, setDeniedModal] = useState(false);

  const urlToken = searchParams.get("token");

  useEffect(() => {
    if (urlToken) {
      const passData = decodeTokenToData(urlToken);
      console.log("Token recibido:", urlToken);
      console.log("Token decodificado:", passData);
      setPassData(passData);
    }
  }, [urlToken]);

  useEffect(() => {
    if (!passData) return;
    if (passData?.receiver_id) {
      getReceiverUserNoToken();
    }
    if (passData?.pass_id) {
      getAuthorizationPass();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passData]);

  const getAuthorizationPass = async () => {
    if (!passData?.pass_id) return;
    try {
      const response = await api.authorizationPassByIdRequest(passData.pass_id);
      setAuthorizationPass(response);
      setConfirmedBy(response?.confirmed_by ?? null);
      if (response?.access_key_qr) {
        setAuthorized(true);
      } else {
        setAuthorized(false);
      }
      if (response?.access_transaction) {
        setDenied(true);
      } else {
        setDenied(false);
      }
      if (response?.visitor_name && response?.visitor_phone) {
        setVisitorInfo({
          name: response?.visitor_name,
          phone: response?.visitor_phone,
        });
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    }
  };

  const getReceiverUserNoToken = async () => {
    if (!passData?.receiver_id) return;
    try {
      const response = await api.userByIdRequestNoToken(passData.receiver_id);
      setReceiverUser(response);
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    }
  };

  const handleAuthorize = async () => {
    if (!passData?.pass_id) return;
    const checkAuthorizationPass = await api.authorizationPassByIdRequest(
      passData.pass_id,
    );
    if (checkAuthorizationPass?.confirmed_by?.id) {
      await getAuthorizationPass();
      return;
    }
    if (authorizationPass?.user) {
      const fullPhone = authorizationPass?.user?.phone;
      getExistedVisitorUser(fullPhone);
    }
    if (visitorInfo) {
      const fullPhone = visitorInfo?.phone;
      getExistedVisitorUser(fullPhone);
    }
  };

  const handleDeny = async () => {
    if (!authorizationPass || !passData?.pass_id || !receiverUser?.id || isLoading) return;
    setIsLoading(true);
    try {
      const checkAuthorizationPass = await api.authorizationPassByIdRequest(
        passData.pass_id,
      );
      if (checkAuthorizationPass?.confirmed_by?.id) {
        await getAuthorizationPass();
        return;
      }
      const userName =
        authorizationPass?.user?.name || authorizationPass?.user?.username || "";
      const visitorName = visitorInfo?.name || "";
      const nameToUse = userName ? userName : visitorName ? visitorName : "";
      const unitName = authorizationPass?.unit?.name || "";
      const currentDateTime = dayjs().toISOString();
      const doorType =
        authorizationPass?.type === "exit" ? "exit" : "entrance";
      const gateId =
        authorizationPass?.access_point?.gates?.find(
          (gate) => gate?.type === doorType,
        )?.id || undefined;

      let accessPayload: CreateAccessPayload = {
        data: {
          property: authorizationPass?.unit?.property?.id || undefined,
          company: authorizationPass?.unit?.property?.company?.id || undefined,
          user: receiverUser?.name,
          unit: unitName,
          visitor: nameToUse,
          authorized: false,
        },
      };
      let transactionPayload: CreateTransactionPayload = {
        data: {
          datetime: currentDateTime,
          access_type: doorType,
          gate: gateId,
        },
      };

      const transactionResponse =
        await api.createTransactionNoToken(transactionPayload);
      if (!transactionResponse?.id) {
        throw new Error("Missing transaction ID in the creation response");
      }
      if (transactionResponse) {
        if (doorType === "entrance") {
          accessPayload = {
            data: {
              ...accessPayload.data,
              entrance_transaction: transactionResponse?.id,
            },
          };
        }
        if (doorType === "exit") {
          accessPayload = {
            data: {
              ...accessPayload.data,
              exit_transaction: transactionResponse?.id,
            },
          };
        }
        const accessResponse = await api.createAccessNoToken(accessPayload);
        if (accessResponse) {
          await api.updateAuthorizationPass(passData.pass_id, {
            data: {
              confirmed_by: receiverUser.id,
              access_transaction: transactionResponse.id,
            },
          });
          setConfirmedBy(receiverUser);
          setDenied(true);
          setAuthorized(false);
          setDeniedModal(true);
          setTimeout(() => {
            setDeniedModal(false);
            navigate("/access-keys");
          }, 2000);
        }
      }
    } catch (error: any) {
      showError(dispatch, error?.response?.data?.error?.message || labels.generic_error);
    } finally {
      setIsLoading(false);
    }
  };

  const createVisitorUser = async () => {
    const user = authorizationPass?.user;
    let payload: OneTimeVisitorPayload = {
      data: {
        name: user ? user?.name : visitorInfo?.name ? visitorInfo?.name : "",
        email: user ? user?.email : "",
        phone: user
          ? user?.phone
          : visitorInfo?.phone
            ? visitorInfo?.phone
            : "",
        created_by_user: Number(receiverUser?.id),
      },
    };
    try {
      setIsLoading(true);
      const createUserResponse = await api.createOneTimeVisitorNoToken(payload);
      if (createUserResponse) {
        createInvitation(createUserResponse?.id);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
    } finally {
      setIsLoading(false);
    }
  };

  const getExistedVisitorUser = async (visitorPhone: string) => {
    if (visitorPhone) {
      try {
        setIsLoading(true);
        const existedUserResponse = await api.getVisitorsByUserNoToken(
          `${receiverUser?.id}`,
          visitorPhone,
        );
        if (existedUserResponse?.data.length > 0) {
          createInvitation(existedUserResponse?.data[0]?.id);
        } else {
          createVisitorUser();
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const createInvitation = async (visitorId: number) => {
    if (visitorId) {
      const isVisitorPass = authorizationPass?.type === "visitor";
      const dateValidFrom = dayjs();
      const dateValidUntil = dateValidFrom.add(15, "minute");
      const accessPoint = authorizationPass?.access_point;

      const finalValidFrom = dateValidFrom
        .second(0)
        .millisecond(0)
        .toISOString();

      const finalValidUntil = dateValidUntil
        .second(0)
        .millisecond(0)
        .toISOString();

      const accessPointsIds = accessPoint?.id ? [accessPoint?.id] : [];

      const payload: CreateAccessKeyRequest = {
        created: receiverUser?.id,
        one_time_visitor: visitorId,
        valid_from: finalValidFrom,
        valid_until: finalValidUntil,
        access_key_type: isVisitorPass ? "visitor" : "authorization",
        ...(isVisitorPass ? { antipassback: 1, access_counter: 1 } : {}),
        reason_for_visit: labels.pass_authorization,
        property: receiverUser?.property?.id,
        unit: receiverUser?.unit?.id,
        access_points: accessPointsIds,
        authorization_passes: authorizationPass?.id
          ? [authorizationPass?.id]
          : [],
        ...(authorizationPass?.type && authorizationPass.type !== "visitor"
          ? { pass_type: authorizationPass.type }
          : {}),
      };
      try {
        setIsLoading(true);
        if (isVisitorPass) {
          const propertyId = authorizationPass?.unit?.property?.id;
          if (!propertyId || !accessPoint?.id) {
            showError(dispatch, labels.authorization_pass_missing_location);
            return;
          }
          const accessGroupIds = await api.visitorAccessGroupsForPassRequest(
            propertyId,
            accessPoint.id,
          );
          if (accessGroupIds.length === 0) {
            showError(dispatch, labels.authorization_pass_no_visitor_groups);
            return;
          }
          payload.access_groups = accessGroupIds;
          payload.property = propertyId;
          payload.unit = authorizationPass?.unit?.id;
        }
        const createdInvitationResponse =
          await api.createAccessKeyRequestNoToken(payload);
        if (createdInvitationResponse && createdInvitationResponse?.id) {
          const verifiablePayload: CreateVerifiableIdPayload = {
            data: {
              type: "qrcode",
              access_key_qr: createdInvitationResponse?.id,
            },
          };
          const verifiableIdResponse =
            await api.createVerifiableIdNoToken(verifiablePayload);
          if (verifiableIdResponse) {
            if (authorizationPass?.id) {
              const updateAuthorizationPassPayload: CreateAuthorizationPassPayload =
                {
                  data: {
                    confirmed_by: receiverUser?.id,
                  },
                };
              await api.updateAuthorizationPass(
                authorizationPass?.documentId,
                updateAuthorizationPassPayload,
              );
            }
            setSendInvitationModal(true);
            setTimeout(() => {
              setSendInvitationModal(false);
              navigate("/access-keys");
            }, 2000);
          }
        }
      } catch (error: any) {
        console.error(error);
        const errorMessage = error?.response?.data?.error?.message;
        showError(dispatch, errorMessage ? errorMessage : labels.generic_error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingY: "20px",
        boxSizing: "border-box",
      }}
    >
      <Modal open={deniedModal} onClose={() => setDeniedModal(false)}>
        <Box
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            style={{
              maxWidth: 350,
              minWidth: 350,
              width: "40%",
              boxSizing: "border-box",
              paddingTop: 20,
              paddingBottom: 20,
              paddingRight: 30,
              paddingLeft: 30,
            }}
          >
            <p className="body-text-lg font-w-500 title-color text-center">
              {labels.denied}
            </p>
            <p
              className="body-text-md mt-4 text-center"
              style={{ fontWeight: 700 }}
            >
              {labels.denied_description}
            </p>
          </Paper>
        </Box>
      </Modal>
      <Modal
        open={sendInvitationModal}
        onClose={() => setSendInvitationModal(false)}
      >
        <Box
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            style={{
              maxWidth: 350,
              minWidth: 350,
              width: "40%",
              boxSizing: "border-box",
              paddingTop: 20,
              paddingBottom: 20,
              paddingRight: 30,
              paddingLeft: 30,
            }}
          >
            <p className="body-text-lg font-w-500 title-color text-center">
              {labels.pass_sended}
            </p>
            <p
              className="body-text-md mt-4 text-center"
              style={{ fontWeight: 700 }}
            >
              {authorizationPass?.user?.name
                ? authorizationPass?.user?.name
                : visitorInfo?.name}
            </p>
          </Paper>
        </Box>
      </Modal>
      <Paper
        variant="outlined"
        className="w-100 p-10 flex flex-dir-col justify-content-center align-items-center border-box"
        sx={{
          maxWidth: 500,
          minHeight: "95vh",
        }}
      >
        <div
          className="w-100 flex flex-dir-col align-items-center justify-content-center gap-6"
          style={{
            paddingTop: 40,
            paddingBottom: 40,
          }}
        >
          {isLoading ? (
            <div className="w-100 h-100 border-box flex align-items-center justify-content-center">
              <CircularProgress />
            </div>
          ) : (
            <>
              <Logo type={"passTrackLogo"} alt="passtrack" className="logo" />
              <p className="main-title mt-8">{labels.pass_authorization}</p>
              {!confirmedBy && (
                <p className="body-text-sm text-center mt-4">
                  {labels.pass_authorization_description}
                </p>
              )}
              {confirmedBy && (
                <>
                  <p
                    className="body-text-md text-center mt-10"
                    style={{ marginBottom: 0, paddingBottom: 0 }}
                  >
                    {labels.pass_already_set}{" "}
                    {authorized && (
                      <span
                        style={{
                          textTransform: "uppercase",
                          fontWeight: 700,
                          color: "#008000",
                        }}
                      >
                        {authorized && labels.authorized}
                      </span>
                    )}
                    {denied && (
                      <span
                        style={{
                          textTransform: "uppercase",
                          fontWeight: 700,
                          color: "#ff0000",
                        }}
                      >
                        {denied && labels.denied}
                      </span>
                    )}{" "}
                    {labels.by_the_user}
                  </p>
                  <p
                    className="body-text-md text-center mb-8"
                    style={{ fontWeight: 700, marginTop: 0, paddingTop: 0 }}
                  >
                    {confirmedBy?.name || confirmedBy?.username}
                  </p>
                </>
              )}
              {/* Information Section */}
              {(authorizationPass?.user ||
                visitorInfo ||
                authorizationPass?.access_point) && (
                <div
                  className="w-100 border-box flex flex-dir-col gap-4 mt-6"
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: 20,
                    borderRadius: 8,
                    maxWidth: 400,
                  }}
                >
                  {/* Visitor or User */}
                  {authorizationPass?.user && (
                    <div className="w-100 border-box">
                      <p className="body-text-sm font-w-700 mb-2">
                        {labels.user}:
                      </p>
                      <p className="body-text-md">
                        {authorizationPass?.user?.name ||
                          authorizationPass?.user?.username}
                      </p>
                    </div>
                  )}
                  {visitorInfo && (
                    <div className="w-100 border-box">
                      <p className="body-text-sm font-w-700 mb-2">
                        {labels.visitor}:
                      </p>
                      <p className="body-text-md">{visitorInfo.name}</p>
                    </div>
                  )}

                  {/* Date time reques */}
                  {authorizationPass?.createdAt && (
                    <>
                      {(authorizationPass?.user || visitorInfo) && <Divider />}
                      <div className="w-100 border-box">
                        <p className="body-text-sm font-w-700 mb-2">
                          {labels.date_time_requested}:
                        </p>
                        <p className="body-text-md">
                          {dayjs(authorizationPass?.createdAt).format(
                            "DD/MM/YYYY hh:mm",
                          )}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Access Point */}
                  {authorizationPass?.access_point && (
                    <>
                      {(authorizationPass?.user || visitorInfo) && <Divider />}
                      <div className="w-100 border-box">
                        <p className="body-text-sm font-w-700 mb-2">
                          {labels.access_point}:
                        </p>
                        <p className="body-text-md">
                          {authorizationPass?.access_point?.name}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Unit */}
                  {authorizationPass?.unit && (
                    <>
                      {(authorizationPass?.user || visitorInfo) && <Divider />}
                      <div className="w-100 border-box">
                        <p className="body-text-sm font-w-700 mb-2">
                          {labels.unit}:
                        </p>
                        <p className="body-text-md">
                          {authorizationPass?.unit?.name}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Gate Type */}
                  {authorizationPass?.type && (
                    <>
                      {(authorizationPass?.user ||
                        visitorInfo ||
                        authorizationPass?.access_point) && <Divider />}
                      <div className="w-100 border-box">
                        <p className="body-text-sm font-w-700 mb-2">
                          {labels.type}:
                        </p>
                        <p className="body-text-md">
                          {labels[authorizationPass?.type]}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              <Stack
                direction="row"
                spacing={2}
                className="w-100 mt-8"
                sx={{ maxWidth: 400 }}
              >
                <Button
                  fullWidth
                  disabled={!!confirmedBy || isLoading || !receiverUser?.id || !authorizationPass}
                  variant="outlined"
                  color="error"
                  size="large"
                  onClick={handleDeny}
                  disableElevation
                >
                  {labels.deny}
                </Button>
                <Button
                  fullWidth
                  disabled={!!confirmedBy || isLoading || !receiverUser?.id || !authorizationPass}
                  variant="contained"
                  size="large"
                  onClick={handleAuthorize}
                  disableElevation
                >
                  {labels.authorize}
                </Button>
              </Stack>
            </>
          )}
        </div>
      </Paper>
    </Container>
  );
};

export default PassAuthorization;
